import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FileText, CheckCircle2, Loader2, AlertCircle, ShieldCheck, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useAppConfig } from "../lib/AppConfigContext";

export default function AdminHandVotePage() {
  const { t } = useTranslation();
  const { appName } = useAppConfig();
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestInfo, setRequestInfo] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [rsaRequired, setRsaRequired] = useState(false);
  const [rsaPrivateKey, setRsaPrivateKey] = useState("");

  useEffect(() => {
    api.get(`/scrutiny/request/${token}`)
      .then(res => {
        setRequestInfo(res.data);
        setRsaRequired(!!res.data.rsa_required);
        if (res.data.status !== 'pending') {
          if (res.data.status === 'admin_approved') toast.info(t('handVote.alreadyApproved'));
          else if (res.data.status === 'completed') toast.info(t('handVote.alreadyCompleted'));
        }
      })
      .catch(() => toast.error(t('handVote.requestNotFound')))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async () => {
    if (rsaRequired && rsaPrivateKey.trim().length < 100) {
      setErrorMsg(t('scrutiny.rsaKeyRequired', "Cal introduir la clau privada RSA"));
      return;
    }
    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = { token };
      if (rsaRequired) payload.admin_rsa_private_key = rsaPrivateKey.trim();
      await api.post("/hand-vote/admin-approve", payload);
      setCompleted(true);
      toast.success(t('handVote.requestApproved'));
    } catch (error) {
      setErrorMsg(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber-600" /></div>;
  if (!requestInfo) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="text-center"><AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" /><h1 className="text-2xl font-bold text-slate-900 mb-2">{t('handVote.requestNotFound')}</h1></div></div>;

  if (completed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="h-10 w-10 text-green-600" /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('handVote.requestApproved', 'Sol·licitud Aprovada')}</h1>
          <p className="text-slate-500 mb-6">{t('handVote.approvedEmailSent', 'S\'ha enviat un email a l\'entitat per completar la generació del fitxer XLSX de vot a mà alçada.')}</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left">
            <p className="text-sm text-slate-600"><strong>{t('common.entity')}:</strong> {requestInfo.entity_name}</p>
            <p className="text-sm text-slate-600"><strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}</p>
          </div>
          <Button onClick={() => navigate('/admin/consultes')} variant="outline" className="w-full mt-4" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('common.back')}
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="h-8 w-8 text-amber-600" /></div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">{t('handVote.xlsxTitle', 'XLSX Vot a Mà Alçada')}</h1>
          <p className="text-slate-500">{t('handVote.adminApproval', 'Aprovació de l\'administrador')}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-800"><strong>{t('common.entity')}:</strong> {requestInfo.entity_name}</p>
          <p className="text-sm text-amber-800"><strong>{t('common.consulta')}:</strong> {requestInfo.consulta_title}</p>
          <p className="text-sm text-amber-800"><strong>{t('common.status')}:</strong> {requestInfo.status === 'pending' ? t('handVote.pendingApproval') : requestInfo.status}</p>
        </div>
        {requestInfo.status === 'pending' ? (
          <div className="space-y-4">
            {rsaRequired ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <ShieldCheck className="h-4 w-4" />
                  <span>{t('scrutiny.rsaMode', 'Mode RSA Offline activat')}</span>
                </div>
                <label className="text-sm font-medium text-slate-700">
                  {t('scrutiny.adminRsaPrivateKey', "Clau Privada RSA de l'Admin")}
                </label>
                <textarea
                  value={rsaPrivateKey}
                  onChange={(e) => setRsaPrivateKey(e.target.value)}
                  rows={6}
                  className="w-full border border-slate-300 rounded-lg p-3 text-xs font-mono focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  data-testid="rsa-private-key-input"
                />
              </div>
            ) : (
              <p className="text-sm text-slate-600">
                {t('handVote.autoKeyDesc', "La clau de l'administrador s'aplicarà automàticament des del servidor.")}
              </p>
            )}
            {errorMsg && <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm" data-testid="hand-vote-error">{errorMsg}</div>}
            <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="admin-hand-vote-approve-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {t('handVote.approveAndSend', 'Aprovar i enviar a l\'entitat')}
            </Button>
          </div>
        ) : (
          <p className="text-center text-slate-500">{t('handVote.alreadyProcessed', 'Aquesta sol·licitud ja ha estat processada')} ({requestInfo.status}).</p>
        )}
      </motion.div>
    </div>
  );
}
